import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { get_dispute_lists } from "../../reduxData/user/userAction";
import { useTranslation } from "react-i18next";
import EmptyData from "../../components/EmptyData";
import { formatDateTime } from "../../hooks/formatDateTime";
import { formatPrice } from "../../hooks/formatPrice";
import CustomPagination from "../../components/CustomPagination";
import TranslatedText from "../../hooks/TranslatedText";
import { DISPUTE_STATUS, disputeStatusLabel, disputeTagClass } from "./disputeStatus";

const DisputedOrder = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { disputeData, totalDispute } = useSelector((state) => state.user);
    const [width, setWidth] = useState(window.innerWidth);
    const [expandedRows, setExpandedRows] = useState({});
    const isMobile = width <= 750;
    const { REACT_APP_IMAGE_URL, REACT_APP_REDIRECT_URL } = process.env;

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchDisputes = async () => {
            await get_dispute_lists(dispatch, 1, 10);
        };
        fetchDisputes();
    }, [user?.token, dispatch]);

    const getImageUrl = (url) => {
        const isExternal = url?.startsWith("http") || url?.startsWith("https");
        return isExternal ? url : url ? `${REACT_APP_IMAGE_URL}${url}` : null;
    };

    const getOrderId = (item) => item?.order_id || item?.orderid || item?.orderId || "-";

    const toggleRow = (disputeId) => {
        setExpandedRows((prev) => ({ ...prev, [disputeId]: !prev[disputeId] }));
    };

    const handleProductDetail = (slug) => {
        if (slug) {
            window.open(
                `${REACT_APP_REDIRECT_URL}auction/${slug}`,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    const renderExpandedItem = (disputeItem, index) => {
        const title = disputeItem?.itemData.title || "-";
        const imageUrl = getImageUrl(
            disputeItem?.variantData?.images?.[0] || disputeItem?.itemData?.ai_images?.[0]
        );
        const itemPrice = disputeItem?.variantData?.bid_amount != 0
            ? disputeItem?.variantData.bid_amount
            : disputeItem?.itemData?.original_retail_price ?? null;

        return (
            <div className="si-td si-sub-full" colSpan={7}>
                <div key={index} className="si-sub-product">
                    <img
                        src={imageUrl}
                        alt={title}
                        className="si-sub-img"
                    />
                    <div className="si-sub-info">
                        <span className="si-sub-title" onClick={() => handleProductDetail(disputeItem?.itemData?.slug)}>
                            {title}
                        </span>
                    </div>
                    <div className="si-sub-chips">
                        {disputeItem.variantData?.size_name && (
                            <span className="od2-attr-chip">
                                {t("dispute.Size")}: <strong>{disputeItem.variantData.size_name}</strong>
                            </span>
                        )}
                        {(disputeItem?.variantData?.color_name) && (
                            <span className="od2-attr-chip">
                                {t("dispute.Color")}: <strong>
                                    <TranslatedText text={disputeItem?.variantData?.color_name} />
                                </strong>
                            </span>
                        )}
                    </div>
                    {itemPrice && (
                        <span className="od2-attr-chip--price">
                            {formatPrice(itemPrice, i18n)}
                        </span>
                    )}
                    {disputeItem?.itemData.quantity && (
                        <span className="od2-attr-chip">
                            {t("dispute.Quantity")}: <strong>{disputeItem?.itemData.quantity}</strong>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const handleDetail = (item) => {
        const orderId = getOrderId(item);
        if (orderId) navigate(`/disputedetail/${item?.id}`, { state: { data: item } });
    };

    // Desktop Table
    const DesktopTable = () => (
        <div className="si-table-wrapper">
            <table className="si-table">
                <colgroup>
                    <col className="si-col-toggle" />
                    <col className="si-col-order" />
                    <col className="si-col-order" />
                    <col className="si-col-items" />
                    <col className="si-col-reason" />
                    <col className="dis-col-status" />
                    <col className="si-col-date" />
                    <col className="si-col-action" />
                </colgroup>

                <thead className="si-thead">
                    <tr>
                        <th className="si-th si-th--center" />
                        <th className="si-th">{t("dispute.DisputeId")}</th>
                        <th className="si-th">{t("dispute.OrderId")}</th>
                        <th className="si-th si-th--center">{t("dispute.NoOfItems")}</th>
                        <th className="si-th">{t("dispute.DisputeReason")}</th>
                        <th className="si-th si-th--center">{t("dispute.Status")}</th>
                        <th className="si-th si-th--center">{t("dispute.DisputeCreatedDate")}</th>
                        <th className="si-th si-th--center">{t("dispute.Action")}</th>
                    </tr>
                </thead>

                <tbody>
                    {disputeData.map((item, index) => {
                        const disputeId = item.id || getOrderId(item);
                        const disputeItems = item?.disputed_items || item?.items || [];
                        const isExpanded = !!expandedRows[disputeId];
                        const hasMultiple = disputeItems.length > 0;
                        const reasonText = <TranslatedText text={item?.comment || item?.seller_comment} />
                        const statusText = item?.status || DISPUTE_STATUS.OPENED;

                        return (
                            <React.Fragment key={disputeId || index}>
                                <tr className={`si-row si-row--parent ${isExpanded ? "si-row--expanded" : ""}`}>
                                    <td className="si-td si-td--center">
                                        {hasMultiple && (
                                            <button
                                                className={`si-toggle-btn ${isExpanded ? "si-toggle-btn--open" : ""}`}
                                                onClick={() => toggleRow(disputeId)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}
                                    </td>
                                    <td className="si-td">
                                        <span className="si-order-id">#{item.id}</span>
                                    </td>
                                    <td className="si-td">
                                        <span className="si-order-id">#{getOrderId(item)}</span>
                                    </td>

                                    <td className="si-td si-td--center">
                                        <button
                                            className={`si-count-badge ${hasMultiple ? "si-count-badge--clickable" : ""}`}
                                            onClick={() => hasMultiple && toggleRow(disputeId)}
                                            disabled={!hasMultiple}
                                        >
                                            {disputeItems.length} {disputeItems.length === 1 ? t("dispute.Item") : t("dispute.Items")}
                                        </button>
                                    </td>

                                    <td className="si-td">
                                        <div className="si-truncated-text">{reasonText}</div>
                                    </td>

                                    <td className="si-td si-td--center">
                                        <span className={`od2-dispute-status-tag ${disputeTagClass(statusText)}`}>
                                            {disputeStatusLabel(statusText, t)}
                                        </span>
                                    </td>

                                    <td className="si-td si-td--center si-date">
                                        {formatDateTime(item?.created_at || item?.buyer_created_at, i18n)}
                                    </td>

                                    <td className="si-td si-td--center">
                                        <button className="si-view-btn" onClick={() => handleDetail(item)}>
                                            {t("dispute.View")}
                                        </button>
                                    </td>
                                </tr>

                                {isExpanded && (
                                    <tr className="si-row si-row--child">
                                        <td className="si-td si-td--indent" />
                                        <td className="si-td si-sub-full" colSpan={7}>
                                            <div className="si-card-items">
                                                {disputeItems.map((disputeItem, idx) => renderExpandedItem(disputeItem, idx))}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    // Mobile Cards
    const MobileCards = () => (
        <div className="si-mobile-list">
            {disputeData.map((item) => {
                const disputeId = item.id || getOrderId(item);
                const disputeItems = item?.disputed_items || item?.items || [];
                const isExpanded = !!expandedRows[disputeId];
                const hasMultiple = disputeItems.length > 0;
                const reasonText = <TranslatedText text={item?.comment || item?.seller_comment} />
                const statusText = item?.status || DISPUTE_STATUS.OPENED;

                return (
                    <div key={disputeId} className="si-card">
                        <div className="si-card-header">
                            <div>
                                <span className="si-card-order-id">#{getOrderId(item)}</span>
                                <div className="si-card-date">
                                    {formatDateTime(item?.created_at || item?.buyer_created_at, i18n)}
                                </div>
                            </div>
                            <div className="si-card-header-right">
                                <span className={`badge px-3 py-2 ${disputeTagClass(statusText)}`}>
                                    {disputeStatusLabel(statusText, t)}
                                </span>
                            </div>
                        </div>

                        <div className="si-card-badges">
                            <div className="si-card-badge-group">
                                <span className="si-card-badge-label">{t("dispute.Reason")}</span>
                                <span className="text-muted">{reasonText}</span>
                            </div>
                        </div>

                        <div className="si-card-badges">
                            <div className="si-card-badge-group">
                                <span className="si-card-badge-label">{t("dispute.NoOfItems")}</span>
                                <span className="badge bg-light text-dark">{disputeItems.length}</span>
                            </div>
                        </div>

                        {hasMultiple && (
                            <button
                                className="si-card-expand-btn"
                                onClick={() => toggleRow(disputeId)}
                            >
                                <svg
                                    width="14" height="14" viewBox="0 0 14 14" fill="none"
                                    className={isExpanded ? "si-icon--flipped" : ""}
                                >
                                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {isExpanded ? t("dispute.HideItems") : `${t("dispute.ShowItems")} ${disputeItems.length} ${t("dispute.Items")}`}
                            </button>
                        )}

                        {isExpanded && (
                            <div className="si-card-items">
                                {disputeItems.map((disputeItem, idx) => renderExpandedItem(disputeItem, idx))}
                            </div>
                        )}

                        <div className="si-card-footer">
                            <button className="si-view-btn" onClick={() => handleDetail(item)}>
                                {t("dispute.View")}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="si-tab-pane">
            <div className="si-section-header">
                <h3 className="si-section-title">
                    <span className="si-section-icon">⚠️</span>
                    {t("dispute.DisputedOrders")}
                </h3>
            </div>

            {totalDispute > 0 ? (
                <>
                    {isMobile ? <MobileCards /> : <DesktopTable />}
                    <div className="mt-4">
                        <CustomPagination
                            total={totalDispute}
                            onPageChange={(page, perPage) => {
                                get_dispute_lists(dispatch, page, perPage);
                            }}
                        />
                    </div>
                </>
            ) : (
                <EmptyData />
            )}
        </div>
    );
};

export default DisputedOrder;
